import { useMutation, useQuery } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import { useCallback, useRef } from 'react';
import { FormProvider } from 'react-hook-form';
import { z } from 'zod';

import { AuthScreenContainer } from '@oui/app-core/src/components/AuthScreenContainer';
import { Button } from '@oui/app-core/src/components/Button';
import { View } from '@oui/app-core/src/components/View';
import { Controller, TextInputRender, useZodForm, useZodFormContext } from '@oui/app-core/src/form';
import { usePersistedState } from '@oui/app-core/src/hooks/usePersistedState';
import { useI18n } from '@oui/app-core/src/lib/i18n';
import { graphql } from '@oui/lib/src/graphql/tada';
import {
  MutationUpdateUserInputSchema,
  PatientSupporterRelationInputSchema,
} from '@oui/lib/src/types';

import { RootStackScreenProps } from '@src/types/navigation';

export const FinishRegisteringQuery = graphql(`
  query FinishRegistering {
    user {
      ID
      givenName
      familyName
      phone
      role {
        ID
        supportee {
          ID
          supportee {
            ID
          }
        }
      }
    }
  }
`);

export const SupportPatientRegistrationMutation = graphql(`
  mutation SupportPatientRegistration(
    $update: MutationUpdateUserInput!
    $patientRelationInput: PatientSupporterRelationInput!
  ) {
    updateUser(input: $update) {
      ID
      givenName
      familyName
      phone
    }
    setPatientSupporterRelation(input: $patientRelationInput) {
      ID
      patientID
      supporterID
      relation
    }
  }
`);

const SupportPatientRegistrationMutationSchema = z.object({
  update: MutationUpdateUserInputSchema.unwrap().pick({ userID: true, user: true }),
  patientRelationInput: PatientSupporterRelationInputSchema,
});

function SupportPatientRegistration() {
  const { $t } = useI18n();
  const { control } = useZodFormContext(SupportPatientRegistrationMutationSchema);

  return (
    <View spacing={12}>
      <Controller
        control={control}
        render={(props) =>
          TextInputRender(props, {
            label: $t({
              id: 'SupportPatientRegistration_givenNameLabel',
              defaultMessage: 'First name',
            }),
          })
        }
        name="update.user.givenName"
      />
      <Controller
        control={control}
        render={(props) =>
          TextInputRender(props, {
            label: $t({
              id: 'SupportPatientRegistration_familyNameLabel',
              defaultMessage: 'Last name',
            }),
          })
        }
        name="update.user.familyName"
      />

      <Controller
        control={control}
        render={(props) =>
          TextInputRender(props, {
            autoComplete: 'tel',
            inputMode: 'tel',
            label: $t({
              id: 'SupportPatientRegistration_phoneNumberLabel',
              defaultMessage: 'Phone number',
            }),
          })
        }
        name="update.user.phone"
      />
    </View>
  );
}

export function FinishRegistering(_props: {}) {
  const { $t } = useI18n();
  const navigation =
    useNavigation<RootStackScreenProps<'FinishPatientRegistration'>['navigation']>();

  const [patientSupporterRelation] = usePersistedState('patientSupporterRelation', 'OTHER');

  const { data } = useQuery(FinishRegisteringQuery);

  const user = data?.user;
  const supporteeID = data?.user?.role?.supportee?.supportee.ID;

  const form = useZodForm(SupportPatientRegistrationMutationSchema, {
    defaultValues: {
      update: {
        userID: user?.ID,
        user: {
          givenName: user?.givenName,
          familyName: user?.familyName,
          phone: user?.phone,
        },
      },
      patientRelationInput: {
        patientID: supporteeID,
        relation: patientSupporterRelation,
      },
    },
  });

  const formRef = useRef(form);
  formRef.current = form;

  const [updateSupportUser, { loading }] = useMutation(SupportPatientRegistrationMutation);

  const onSave = useCallback(async () => {
    await formRef.current.handleSubmit(async (data) => {
      await updateSupportUser({
        variables: data,
      });
      navigation.navigate('Confidentiality');
    })();
  }, [updateSupportUser, navigation]);

  return (
    <AuthScreenContainer
      heading={$t({ id: 'FinishRegistering_title', defaultMessage: 'Finish registering' })}
    >
      <View
        style={{
          marginTop: 20,
        }}
      >
        <FormProvider {...form}>
          {!user || !data ? null : (
            <View spacing={20}>
              <SupportPatientRegistration />
              <Button
                alignSelf="center"
                testID="FinishRegistering_submitButton"
                onPress={onSave}
                disabled={loading}
                text={$t({ id: 'FinishRegistering_submitButton', defaultMessage: 'Done' })}
              />
            </View>
          )}
        </FormProvider>
      </View>
    </AuthScreenContainer>
  );
}
